// src/pages/BoneBeat.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Müzik Linki Derleme & Paylaşım Merkezi  ·  BoneBeat
// Veri: Firebase Realtime DB /playlists
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useNavigate }                 from "react-router-dom";
import { useTranslation }              from "react-i18next";
import { ref, onValue, push, update, remove } from "firebase/database";
import { db }                          from "../firebase/config";
import { useAuth }                     from "../context/AuthContext";
import CommentSection                  from "../components/CommentSection";
import SidebarBeat                     from "../components/Sidebar/SidebarBeat.jsx";
import { SidebarBeatProvider }         from "../context/SidebarBeatContext.jsx";
import { cleanContentUrl }             from "../utils/cleanContentUrl";
import { filterText }                  from "../utils/profanityFilter";

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK: uid ile canlı kullanıcı bilgisi — isim/foto değişince otomatik güncellenir
// ═══════════════════════════════════════════════════════════════════════════════
function useUserInfo(uid) {
  const [info, setInfo] = useState({ displayName: null, photoURL: null });
  useEffect(() => {
    if (!uid) return;
    const r = ref(db, `publicProfiles/${uid}`);
    const unsub = onValue(r, snap => {
      if (snap.exists()) {
        const d = snap.val();
        setInfo({ displayName: d.displayName || null, photoURL: d.photoURL || null });
      }
    });
    return () => unsub();
  }, [uid]);
  return info;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SABITLER
// ═══════════════════════════════════════════════════════════════════════════════

const C = {
  bg:         "#0d0a05",
  bgCard:     "#12100a",
  bgDeep:     "#0a0803",
  border:     "rgba(212,168,83,0.12)",
  borderHov:  "rgba(212,168,83,0.3)",
  gold:       "#d4a853",
  goldDim:    "rgba(212,168,83,0.5)",
  goldGhost:  "var(--bone-text-muted)",
  goldFaint:  "rgba(212,168,83,0.08)",
  ivory:      "#e8d5b0",
  ivoryDim:   "rgba(232,213,176,0.6)",
  ivoryGhost: "rgba(232,213,176,0.25)",
  red:        "#e05c5c",
  redDim:     "rgba(224,92,92,0.6)",
  green:      "#6ec97a",
};

const ALLOWED_PLATFORMS = [
  { name: "Spotify",       regex: /^https?:\/\/(open\.)?spotify\.com\// },
  { name: "Apple Music",   regex: /^https?:\/\/music\.apple\.com\// },
  { name: "YouTube Music", regex: /^https?:\/\/music\.youtube\.com\// },
  { name: "YouTube",       regex: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\// },
  { name: "Deezer",        regex: /^https?:\/\/(www\.)?deezer\.com\// },
  { name: "Amazon Music",  regex: /^https?:\/\/music\.amazon\.(com|co\.\w+)\// },
  { name: "SoundCloud",    regex: /^https?:\/\/(www\.)?soundcloud\.com\// },
  { name: "TIDAL",         regex: /^https?:\/\/(www\.)?tidal\.com\// },
  { name: "Pandora",       regex: /^https?:\/\/(www\.)?pandora\.com\// },
  { name: "Qobuz",         regex: /^https?:\/\/(www\.)?qobuz\.com\// },
  { name: "JioSaavn",      regex: /^https?:\/\/(www\.)?jiosaavn\.com\// },
  { name: "Yandex Music",  regex: /^https?:\/\/music\.yandex\.(ru|com)\// },
  { name: "KuGou",         regex: /^https?:\/\/(www\.)?kugou\.com\// },
  { name: "Naver Vibe",    regex: /^https?:\/\/vibe\.naver\.com\// },
  { name: "Wynk Music",    regex: /^https?:\/\/(www\.)?wynk\.in\// },
  { name: "Moov",          regex: /^https?:\/\/(www\.)?moov\.hk\// },
];

function detectPlatform(url) {
  for (const p of ALLOWED_PLATFORMS) { if (p.regex.test(url)) return p.name; }
  return null;
}
function isValidMusicUrl(url) { return !!detectPlatform(url); }

const PLATFORM_ICONS = {
  "Spotify": "♫", "Apple Music": "♪", "YouTube Music": "▶", "YouTube": "▶",
  "Deezer": "◉", "Amazon Music": "♬", "SoundCloud": "☁", "TIDAL": "◈",
  "Pandora": "◎", "Qobuz": "✦", "JioSaavn": "◆", "Yandex Music": "◇",
  "KuGou": "♩", "Naver Vibe": "♪", "Wynk Music": "♫", "Moov": "◉",
};

// ─── Zaman yardımcısı ─────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "şimdi";
  if (m < 60) return `${m}dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}sa önce`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}g önce`;
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

// Tam tarih (kart alt satırı için)
function fullDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI BİLEŞENLERİ
// ═══════════════════════════════════════════════════════════════════════════════

function Chip({ children, color = C.goldDim }) {
  return (
    <span style={{
      fontFamily: "'Courier Prime',monospace", fontSize: 8, letterSpacing: "0.22em",
      textTransform: "uppercase", color, border: `1px solid ${color}`,
      padding: "2px 7px", lineHeight: 1.4, borderRadius: 6,
    }}>{children}</span>
  );
}

function IconBtn({ onClick, title, danger, children, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 6,
        color: danger ? (hov ? C.red : C.redDim) : (hov ? C.gold : C.goldDim),
        padding: "4px 6px", display: "flex", alignItems: "center",
        transition: "color 0.2s", opacity: disabled ? 0.4 : 1,
      }}>
      {children}
    </button>
  );
}

function PrimaryBtn({ onClick, children, disabled, small }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: disabled ? "rgba(212,168,83,0.25)" : hov ? "#c49840" : C.gold,
        border: "none", borderRadius: 8, color: "#0d0a05",
        padding: small ? "8px 16px" : "11px 22px",
        fontFamily: "'Courier Prime',monospace", fontWeight: "bold",
        fontSize: small ? 10 : 11, letterSpacing: "0.2em", textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s", opacity: disabled ? 0.7 : 1,
      }}>
      {children}
    </button>
  );
}

function GhostBtn({ onClick, children, danger }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: "transparent",
        border: `1px solid ${danger ? (hov ? C.red : C.redDim) : (hov ? C.gold : C.border)}`,
        borderRadius: 8,
        color: danger ? (hov ? C.red : C.redDim) : (hov ? C.gold : C.goldDim),
        padding: "8px 16px", fontFamily: "'Courier Prime',monospace",
        fontWeight: "bold", fontSize: 10, letterSpacing: "0.18em",
        textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
      }}>
      {children}
    </button>
  );
}

function TextInput({ value, onChange, placeholder, disabled, onKeyDown }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder}
      disabled={disabled} onKeyDown={onKeyDown}
      style={{
        display: "block", width: "100%", boxSizing: "border-box",
        padding: "10px 13px", borderRadius: 6,
        background: disabled ? "rgba(212,168,83,0.03)" : "#1a1208",
        border: `1px solid ${C.border}`,
        color: disabled ? C.goldDim : C.ivory,
        fontFamily: "'Courier Prime',monospace", fontSize: 12,
        outline: "none", letterSpacing: "0.04em",
      }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAYLIST KARTI — sosyal detaylar: paylaşan + tarih
// ═══════════════════════════════════════════════════════════════════════════════

function PlaylistCard({ playlist, onOpen, onDelete, onBlockUser, onOpenProfile, isAdmin, currentUid }) {
  const navigate  = useNavigate();
  const isOwner   = playlist.uid === currentUid;
  const songCount = playlist.songs ? Object.keys(playlist.songs).length : 0;
  const liveUser  = useUserInfo(playlist.uid); // ← canlı isim + foto
  const userName  = liveUser.displayName || playlist.userName || "Anonim";
  const photoURL  = liveUser.photoURL    || playlist.photoURL || null;

  // /profile/:uid sayfasına yönlendirme
  function handleProfileClick(e) {
    e.stopPropagation();
    navigate(`/profile/${playlist.uid}`);
  }

  return (
    <div
      onClick={() => onOpen(playlist)}
      style={{
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: "20px 22px", cursor: "pointer",
        transition: "all 0.25s ease", position: "relative",
        display: "flex", flexDirection: "column", gap: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.background = "#161209"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.background = C.bgCard;  }}
    >
      {/* Üst satır: başlık + aksiyon butonları */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700,
            color: C.ivory, letterSpacing: "0.04em", marginBottom: 2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {playlist.playlistName || "İsimsiz Derleme"}
          </div>
        </div>

        {/* Admin / sahip aksiyonları */}
        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {isAdmin && !isOwner && (
            <IconBtn onClick={() => onBlockUser(playlist.uid, playlist.userName)} title="Kullanıcıyı engelle" danger>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" />
              </svg>
            </IconBtn>
          )}
          {(isAdmin || isOwner) && (
            <IconBtn onClick={() => onDelete(playlist.id)} title="Sil" danger>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </IconBtn>
          )}
        </div>
      </div>

      {/* ── Sosyal satır: "Paylaşan: ..." + tarih ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12, gap: 8, flexWrap: "wrap",
      }}>
        {/* Paylaşan kullanıcı */}
        <div
          onClick={handleProfileClick}
          title={`${userName} profilini gör`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontFamily: "'Courier Prime',monospace", fontSize: 10,
            color: C.goldDim, letterSpacing: "0.08em",
            cursor: "pointer", flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = C.gold}
          onMouseLeave={e => e.currentTarget.style.color = C.goldDim}
        >
          {/* Küçük avatar dairesi */}
          <div style={{
            width: 18, height: 18, borderRadius: "50%",
            border: `1px solid ${C.goldGhost}`,
            overflow: "hidden", flexShrink: 0,
            background: C.goldFaint,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 9, fontWeight: 700, color: C.goldDim }}>
                  {userName[0].toUpperCase()}
                </span>
            }
          </div>
          <span>
            <span style={{ color: "rgba(212,168,83,0.35)", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase" }}>Paylaşan </span>
            <span style={{ textDecoration: "underline", textDecorationColor: "rgba(212,168,83,0.25)", textUnderlineOffset: 3 }}>
              {userName}
            </span>
          </span>
        </div>

        {/* Tarih */}
        {playlist.createdAt && (
          <span style={{
            fontFamily: "'Courier Prime',monospace", fontSize: 9,
            color: "rgba(212,168,83,0.28)", letterSpacing: "0.08em",
            flexShrink: 0,
          }} title={fullDate(playlist.createdAt)}>
            {timeAgo(playlist.createdAt)}
          </span>
        )}
      </div>

      {/* Platform ikonları */}
      {playlist.songs && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
          {[...new Set(
            Object.values(playlist.songs)
              .map(s => detectPlatform(s.url || ""))
              .filter(Boolean)
          )].map(p => (
            <span key={p} style={{ fontSize: 11, color: C.goldGhost }} title={p}>
              {PLATFORM_ICONS[p]}
            </span>
          ))}
        </div>
      )}

      {/* Alt satır: şarkı sayısı */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <Chip>{songCount} şarkı</Chip>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAYLIST DETAY
// ═══════════════════════════════════════════════════════════════════════════════

function PlaylistDetail({ playlist, onClose, currentUid, isAdmin, onRefresh, onOpenProfile }) {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const isOwner  = playlist.uid === currentUid;
  const canEdit  = isOwner || isAdmin;
  const songsArr = playlist.songs
    ? Object.entries(playlist.songs).map(([k, v]) => ({ _key: k, ...v }))
    : [];

  const [songs,       setSongs]       = useState(songsArr);
  const [name,        setName]        = useState(playlist.playlistName || "");
  const [newSongName, setNewSongName] = useState("");
  const [newSongUrl,  setNewSongUrl]  = useState("");
  const [urlError,    setUrlError]    = useState("");
  const [saving,      setSaving]      = useState(false);
  const [msg,         setMsg]         = useState("");

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const songsObj = {};
      songs.forEach(s => { songsObj[s._key] = { name: filterText(s.name), url: s.url }; });
      await update(ref(db, `playlists/${playlist.id}`), { playlistName: filterText(name.trim()), songs: songsObj });
      console.log("[BoneBeat] Playlist güncellendi:", playlist.id);
      setMsg("Kaydedildi ✓");
      setTimeout(() => { setMsg(""); onRefresh(); }, 1200);
    } catch (err) {
      console.error("[BoneBeat] Güncelleme hatası:", err.code, err.message);
      if (err.code === "PERMISSION_DENIED") {
        setMsg("İzin hatası: Bu kaydı düzenleme yetkiniz yok.");
      } else {
        setMsg(`Hata oluştu (${err.code || "bilinmeyen"})`);
      }
    }
    setSaving(false);
  }

  function addSong() {
    setUrlError("");
    if (!newSongUrl.trim()) { setUrlError("URL zorunludur."); return; }
    const result = cleanContentUrl(newSongUrl);
    if (!result.isValid) { setUrlError(result.error); return; }
    if (!isValidMusicUrl(result.url)) { setUrlError("Sadece izin verilen müzik platformları kabul edilir."); return; }
    const key = `s${Date.now()}`;
    setSongs(prev => [...prev, { _key: key, name: filterText(newSongName.trim() || result.url), url: result.url }]);
    setNewSongName(""); setNewSongUrl("");
  }
  function removeSong(key) { setSongs(prev => prev.filter(s => s._key !== key)); }
  function moveUp(i)   { if (i === 0) return;               setSongs(prev => { const a = [...prev]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; }); }
  function moveDown(i) { if (i === songs.length - 1) return; setSongs(prev => { const a = [...prev]; [a[i], a[i+1]] = [a[i+1], a[i]]; return a; }); }

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      {/* Başlık çubuğu */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: "var(--bone-text-accent)", cursor: "pointer",
          fontFamily: "'Courier Prime',monospace", fontSize: 11, letterSpacing: "0.15em", padding: 0,
        }}>← Geri</button>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        {/* Paylaşan: tıklanabilir → /profile/:uid */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            onClick={() => navigate(`/profile/${playlist.uid}`)}
            style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.22em",
              color: C.goldGhost, textTransform: "uppercase", cursor: "pointer",
              textDecoration: "underline", textDecorationColor: "rgba(212,168,83,0.2)",
              textUnderlineOffset: 3,
            }}
            onMouseEnter={e => e.currentTarget.style.color = C.goldDim}
            onMouseLeave={e => e.currentTarget.style.color = C.goldGhost}
          >
            {playlist.userName}
          </div>
          <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(212,168,83,0.2)" }}>·</span>
          <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(212,168,83,0.2)" }} title={fullDate(playlist.createdAt)}>
            {timeAgo(playlist.createdAt)}
          </span>
        </div>
      </div>

      {/* Playlist adı */}
      {canEdit
        ? <TextInput value={name} onChange={e => setName(e.target.value)} placeholder="Playlist adı" />
        : <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, color: C.gold, margin: "0 0 20px" }}>{name}</h2>
      }

      {/* Şarkı listesi */}
      <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 16, marginBottom: 20 }}>
        {songs.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost }}>
            Henüz şarkı eklenmedi.
          </div>
        )}
        {songs.map((song, i) => {
          const platform = detectPlatform(song.url || "");
          return (
            <div key={song._key} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
              borderBottom: i < songs.length - 1 ? `1px solid ${C.goldFaint}` : "none",
            }}>
              {/* Sıra numarası */}
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.goldGhost, width: 18, textAlign: "right", flexShrink: 0 }}>{i + 1}</span>

              {/* ▶ Play butonu — linke tıklanınca platformu açar */}
              <a
                href={song.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                title={`${platform || "Linki"} aç`}
                style={{
                  width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(212,168,83,0.08)",
                  border: "1px solid rgba(212,168,83,0.18)",
                  color: C.goldDim, textDecoration: "none", fontSize: 10,
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(212,168,83,0.18)"; e.currentTarget.style.borderColor = C.goldDim; e.currentTarget.style.color = C.gold; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(212,168,83,0.08)"; e.currentTarget.style.borderColor = "rgba(212,168,83,0.18)"; e.currentTarget.style.color = C.goldDim; }}
              >
                ▶
              </a>

              {/* Şarkı adı + platform etiketi */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: C.ivory, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{song.name}</div>
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, letterSpacing: "0.06em" }}>
                  {platform || "Bağlantı"}
                </span>
              </div>
              {platform && <Chip color="rgba(212,168,83,0.28)">{platform}</Chip>}
              {canEdit && (
                <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                  <IconBtn onClick={() => moveUp(i)}   title="Yukarı taşı"  disabled={i === 0}>▲</IconBtn>
                  <IconBtn onClick={() => moveDown(i)} title="Aşağı taşı"  disabled={i === songs.length - 1}>▼</IconBtn>
                  <IconBtn onClick={() => removeSong(song._key)} title="Şarkıyı sil" danger>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </IconBtn>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Şarkı ekleme formu */}
      {canEdit && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.25em", color: C.goldGhost, textTransform: "uppercase", marginBottom: 14 }}>✦ Şarkı Ekle</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}><TextInput value={newSongName} onChange={e => setNewSongName(e.target.value)} placeholder={t('bonebeat.form.songNamePlaceholder')} /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TextInput value={newSongUrl} onChange={e => { setNewSongUrl(e.target.value); setUrlError(""); }} placeholder="Müzik linki..." onKeyDown={e => e.key === "Enter" && addSong()} />
            </div>
            <PrimaryBtn onClick={addSong} small>{t('bonebeat.form.add')}</PrimaryBtn>
          </div>
          {urlError && <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.red, margin: "6px 0 0" }}>⚠ {urlError}</p>}
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, margin: "8px 0 0", letterSpacing: "0.06em" }}>
            Kabul edilen: Spotify, Apple Music, YouTube Music, Deezer, Amazon Music, SoundCloud, TIDAL, Pandora, Qobuz, JioSaavn, Yandex Music, KuGou, Naver Vibe, Wynk, Moov
          </p>
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}</PrimaryBtn>
          {msg && <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.green }}>{msg}</span>}
        </div>
      )}

      <CommentSection type="playlists" postId={playlist.id} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// YENİ PLAYLIST FORMU
// ═══════════════════════════════════════════════════════════════════════════════

function NewPlaylistForm({ currentUser, currentUserData, onSuccess }) {
  const [playlistName, setPlaylistName] = useState("");
  const [songs,        setSongs]        = useState([]);
  const [newSongName,  setNewSongName]  = useState("");
  const [newSongUrl,   setNewSongUrl]   = useState("");
  const [urlError,     setUrlError]     = useState("");
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState("");
  const { t } = useTranslation();

  function addSong() {
    setUrlError("");
    if (!newSongUrl.trim()) { setUrlError(t('bonebeat.form.urlRequired')); return; }
    const result = cleanContentUrl(newSongUrl);
    if (!result.isValid) { setUrlError(result.error); return; }
    if (!isValidMusicUrl(result.url)) { setUrlError(t('bonebeat.form.unsupported')); return; }
    setSongs(prev => [...prev, { _key: `s${Date.now()}`, name: filterText(newSongName.trim() || result.url), url: result.url }]);
    setNewSongName(""); setNewSongUrl("");
  }
  function removeSong(key) { setSongs(prev => prev.filter(s => s._key !== key)); }
  function moveUp(i)   { if (i === 0) return;               setSongs(prev => { const a = [...prev]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; }); }
  function moveDown(i) { if (i === songs.length - 1) return; setSongs(prev => { const a = [...prev]; [a[i], a[i+1]] = [a[i+1], a[i]]; return a; }); }

  async function handleCreate() {
    if (!playlistName.trim()) { setMsg(t('bonebeat.form.nameRequired')); return; }
    if (songs.length === 0)   { setMsg(t('bonebeat.form.songRequired')); return; }

    // ── Güvenlik: kullanıcı oturumu yoksa yazmayı engelle ────────────────
    if (!currentUser?.uid) {
      setMsg("Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
      console.error("[BoneBeat] handleCreate: currentUser veya uid yok.", currentUser);
      return;
    }

    setSaving(true); setMsg("");
    try {
      const songsObj = {};
      songs.forEach(s => { songsObj[s._key] = { name: filterText(s.name), url: s.url }; });

      const payload = {
        uid:          currentUser.uid,
        userName:     currentUser.displayName || currentUser.email || "Anonim",
        photoURL:     currentUserData?.photoURL || currentUser.photoURL || "",
        playlistName: filterText(playlistName.trim()),
        songs:        songsObj,
        createdAt:    Date.now(),
      };

      // ── Konsola yaz (geliştirme kolaylığı) ───────────────────────────
      console.log("[BoneBeat] Yeni playlist oluşturuluyor:", payload);

      await push(ref(db, "playlists"), payload);

      console.log("[BoneBeat] Playlist başarıyla kaydedildi.");
      setMsg(t('bonebeat.form.created'));
      setTimeout(() => { setPlaylistName(""); setSongs([]); setMsg(""); onSuccess(); }, 1400);

    } catch (err) {
      // ── Hatanın tam detayını konsola bas ─────────────────────────────
      console.error("[BoneBeat] Playlist oluşturma hatası:", {
        code:    err.code,
        message: err.message,
        uid:     currentUser?.uid,
        path:    "playlists",
      });

      // Kullanıcıya anlamlı hata mesajı
      if (err.code === "PERMISSION_DENIED") {
        setMsg("İzin hatası: Firebase kuralları bu işlemi reddetti. Lütfen giriş durumunuzu kontrol edin.");
      } else if (err.code === "NETWORK_ERROR" || err.message?.includes("network")) {
        setMsg("Ağ hatası: İnternet bağlantınızı kontrol edin ve tekrar deneyin.");
      } else {
        setMsg(`${t('bonebeat.form.error')} (${err.code || "bilinmeyen hata"})`);
      }
    }
    setSaving(false);
  }

  const cleanedSongUrl = newSongUrl ? cleanContentUrl(newSongUrl) : null;
  const detectedPlatform = cleanedSongUrl?.isValid ? detectPlatform(cleanedSongUrl.url) : null;

  // Desteklenen platformlar — ikon + renk ile
  const PLATFORM_GRID = [
    { name: "Spotify",       color: "#1ed760" },
    { name: "Apple Music",   color: "#fc3c44" },
    { name: "YouTube Music", color: "#ff0000" },
    { name: "YouTube",       color: "#ff0000" },
    { name: "Deezer",        color: "#a238ff" },
    { name: "SoundCloud",    color: "#ff5500" },
    { name: "Amazon Music",  color: "#00a8e0" },
    { name: "TIDAL",         color: "#00ffff" },
    { name: "Pandora",       color: "#224099" },
    { name: "Qobuz",         color: "#00c2ff" },
    { name: "JioSaavn",      color: "#2bc5b4" },
    { name: "Yandex Music",  color: "#ffcc00" },
    { name: "KuGou",         color: "#1490f5" },
    { name: "Naver Vibe",    color: "#04cf5c" },
    { name: "Wynk Music",    color: "#ff3366" },
    { name: "Moov",          color: "#f5a623" },
  ];

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>

      {/* Başlık */}
      <div style={{ marginBottom: 32 }}>
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.35em", color: C.goldGhost, textTransform: "uppercase", marginBottom: 6 }}>✦ {t('bonebeat.form.eyebrow')} ✦</p>
        <h2 style={{ fontFamily: "'Playfair Display',serif", color: C.gold, fontSize: 28, fontWeight: 700, margin: "0 0 8px" }}>{t('bonebeat.form.title')}</h2>
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost, letterSpacing: "0.06em" }}>
          {t('bonebeat.form.subtitle')}
        </p>
      </div>

      {/* Liste adı */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.28em", color: C.goldDim, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
          {t('bonebeat.form.nameLabel')} <span style={{ color: C.red, marginLeft: 2 }}>*</span>
        </label>
        <TextInput
          value={playlistName}
          onChange={e => setPlaylistName(e.target.value)}
          placeholder={t('bonebeat.form.namePlaceholder')}
        />
      </div>

      {/* Eklenen şarkılar */}
      {songs.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.28em", color: C.goldDim, textTransform: "uppercase" }}>{t('bonebeat.form.added')}</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost }}>{songs.length} {t('bonebeat.form.tracks')}</span>
          </div>
          <div style={{ background: C.bgDeep, border: `1px solid ${C.border}` }}>
            {songs.map((song, i) => {
              const p = detectPlatform(song.url || "");
              return (
                <div key={song._key} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "11px 16px",
                  borderBottom: i < songs.length - 1 ? `1px solid ${C.goldFaint}` : "none",
                }}>
                  <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.goldGhost, width: 20, textAlign: "right", flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ fontSize: 13, width: 18, textAlign: "center", flexShrink: 0 }}>{p ? PLATFORM_ICONS[p] : "·"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: C.ivory, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{song.name}</div>
                    {p && <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, marginTop: 2 }}>{p}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <IconBtn onClick={() => moveUp(i)}            disabled={i === 0}              title="Yukarı">▲</IconBtn>
                    <IconBtn onClick={() => moveDown(i)}          disabled={i === songs.length - 1} title="Aşağı">▼</IconBtn>
                    <IconBtn onClick={() => removeSong(song._key)} danger title="Kaldır">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                    </IconBtn>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Şarkı ekleme */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, padding: "22px 24px", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.28em", color: C.goldDim, textTransform: "uppercase", marginBottom: 16 }}>{`✦ ${t('bonebeat.form.addSong')}`}</div>

        <div style={{ marginBottom: 10 }}>
          <TextInput
            value={newSongName}
            onChange={e => setNewSongName(e.target.value)}
            placeholder={t('bonebeat.form.songNamePlaceholder')}
          />
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <TextInput
              value={newSongUrl}
              onChange={e => { setNewSongUrl(e.target.value); setUrlError(""); }}
              placeholder="https://open.spotify.com/track/..."
              onKeyDown={e => e.key === "Enter" && addSong()}
            />
            {/* Canlı platform algılama */}
            {newSongUrl && !detectedPlatform && !urlError && (
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.redDim, margin: "5px 0 0" }}>
                ⚠ {t('bonebeat.form.unsupportedHint')}
              </p>
            )}
            {newSongUrl && detectedPlatform && (
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.green, margin: "5px 0 0" }}>
                ✓ {detectedPlatform} {t('bonebeat.form.detected')}
              </p>
            )}
          </div>
          <PrimaryBtn onClick={addSong} small>{t('bonebeat.form.add')}</PrimaryBtn>
        </div>
        {urlError && (
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.red, margin: "8px 0 0" }}>⚠ {urlError}</p>
        )}
      </div>

      {/* Desteklenen platformlar */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.28em", color: "rgba(212,168,83,0.22)", textTransform: "uppercase" }}>{t('bonebeat.form.supportedPlatforms')}</span>
          <div style={{ flex: 1, height: 1, background: "rgba(212,168,83,0.06)" }} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {PLATFORM_GRID.map(p => (
            <span key={p.name} style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 9,
              letterSpacing: "0.1em", padding: "3px 9px",
              border: `1px solid ${p.color}22`,
              color: `${p.color}bb`,
              background: `${p.color}0d`,
            }}>
              {PLATFORM_ICONS[p.name]} {p.name}
            </span>
          ))}
        </div>
      </div>

      {/* Mesaj + Yayınla butonu */}
      {msg && (
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: msg.includes("✓") ? C.green : C.red, marginBottom: 14 }}>
          {msg}
        </p>
      )}
      <PrimaryBtn onClick={handleCreate} disabled={saving}>
        {saving ? t('bonebeat.form.creating') : `✦ ${t('bonebeat.form.publish')}`}
      </PrimaryBtn>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// KULLANICI PROFİLİ (inline — /bonebeat içi)
// ═══════════════════════════════════════════════════════════════════════════════

function UserProfile({ profileUser, playlists, onBack, onOpen, onDelete, onBlockUser, isAdmin, currentUid }) {
  const isOwnProfile = profileUser.uid === currentUid;
  const { t } = useTranslation();
  const liveUser = useUserInfo(profileUser.uid);
  const userName = liveUser.displayName || profileUser.userName || t('bonebeat.anonymousUser');
  const photoURL = liveUser.photoURL    || profileUser.photoURL || null;
  const initial  = userName.trim()[0].toUpperCase();

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <button onClick={onBack} style={{
          background: "none", border: "none", color: "var(--bone-text-accent)", cursor: "pointer",
        fontFamily: "'Courier Prime',monospace", fontSize: 11, letterSpacing: "0.18em",
        padding: 0, marginBottom: 36, display: "flex", alignItems: "center", gap: 6,
      }}>← {t('bonebeat.backToFeed')}</button>

      <div style={{
        background: C.bgCard, border: `1px solid ${C.border}`,
        padding: "32px 36px", marginBottom: 36,
        display: "flex", alignItems: "center", gap: 24,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: -40, right: -40, width: 180, height: 180, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(212,168,83,0.06) 0%,transparent 70%)",
        }} />
        <div style={{
          width: 64, height: 64, borderRadius: "50%", border: `1px solid ${C.border}`, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: C.bgDeep, overflow: "hidden",
        }}>
          {photoURL
            ? <img src={photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: C.goldDim }}>{initial}</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: C.ivory, margin: 0, letterSpacing: "0.04em" }}>
              {userName}
            </h2>
            {isOwnProfile && <Chip color={C.goldDim}>{t('admin.you')}</Chip>}
          </div>
          <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.goldGhost, letterSpacing: "0.18em" }}>
            {playlists.length} {t('bonebeat.compilations')} · BoneCast
          </div>
        </div>
        {isAdmin && !isOwnProfile && (
          <div style={{ flexShrink: 0 }}>
            <GhostBtn danger onClick={() => onBlockUser(profileUser.uid, profileUser.userName)}>{t('admin.block')}</GhostBtn>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.32em", color: C.goldGhost, textTransform: "uppercase" }}>✦ {t('bonebeat.compilationsLabel')} ✦</span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, letterSpacing: "0.15em" }}>{playlists.length} {t('bonebeat.listCount')}</span>
      </div>

      {playlists.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.goldDim, marginBottom: 6 }}>{t('bonebeat.noCompilations')}</p>
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost }}>{t('bonebeat.noCompilationsHint')}</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {playlists.map(pl => (
            <PlaylistCard
              key={pl.id} playlist={pl} onOpen={onOpen} onOpenProfile={() => {}}
              onDelete={id => onDelete(id)} onBlockUser={onBlockUser}
              isAdmin={isAdmin} currentUid={currentUid}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODERATÖR PANELİ
// ═══════════════════════════════════════════════════════════════════════════════

function ModerationPanel({ playlists, onDelete, onBlockUser }) {
  const [search, setSearch] = useState("");
  const { t } = useTranslation();
  const filtered = playlists.filter(p =>
    (p.playlistName || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.userName     || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.uid          || "").includes(search)
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.35em", color: "rgba(224,92,92,0.4)", textTransform: "uppercase", marginBottom: 6 }}>⚑ Admin</p>
        <h2 style={{ fontFamily: "'Playfair Display',serif", color: C.red, fontSize: 26, fontWeight: 700, margin: 0 }}>{t('bonebeat.mod.title')}</h2>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: t('bonebeat.mod.totalPlaylists'), val: playlists.length },
          { label: t('bonebeat.mod.uniqueUsers'),    val: new Set(playlists.map(p => p.uid)).size },
          { label: t('bonebeat.mod.totalSongs'),     val: playlists.reduce((acc, p) => acc + (p.songs ? Object.keys(p.songs).length : 0), 0) },
        ].map(stat => (
          <div key={stat.label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, padding: "14px 20px", flex: 1, minWidth: 120 }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: C.gold }}>{stat.val}</div>
            <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, letterSpacing: "0.15em", textTransform: "uppercase" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder={t('bonebeat.mod.searchPlaceholder')} />
      </div>

      <div style={{ background: C.bgDeep, border: `1px solid ${C.border}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 80px", padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
          {[t('bonebeat.mod.colPlaylist'), t('bonebeat.mod.colBy'), t('bonebeat.mod.colSongs'), t('bonebeat.mod.colAction')].map(h => (
            <span key={h} style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, color: C.goldGhost, letterSpacing: "0.25em", textTransform: "uppercase" }}>{h}</span>
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost }}>{t('bonebeat.mod.noResults')}</div>
        )}
        {filtered.map((p, i) => {
          const songCount = p.songs ? Object.keys(p.songs).length : 0;
          return (
            <div key={p.id} style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr 80px",
              padding: "12px 16px", alignItems: "center",
              borderBottom: i < filtered.length - 1 ? `1px solid ${C.goldFaint}` : "none",
            }}>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: C.ivory, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.playlistName || "—"}</span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.userName || "—"}</span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost }}>{songCount}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <IconBtn onClick={() => onBlockUser(p.uid, p.userName)} title={t('bonebeat.mod.blockUser')} danger>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" /></svg>
                </IconBtn>
                <IconBtn onClick={() => onDelete(p.id)} title={t('common.delete')} danger>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                </IconBtn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA BİLEŞEN — BoneBeatInner
// ═══════════════════════════════════════════════════════════════════════════════

function BoneBeatInner() {
  const { user, userData, isAdmin, authLoading = false } = useAuth();
  const { t } = useTranslation();

  const [view,          setView]          = useState("feed");
  const [playlists,     setPlaylists]     = useState([]);
  // loading: true yalnızca Firebase'den ilk yanıt gelmeden önce
  const [loading,       setLoading]       = useState(true);
  const [dbError,       setDbError]       = useState(null);   // Firebase okuma hatası
  const [openPlaylist,  setOpenPlaylist]  = useState(null);
  const [profileUser,   setProfileUser]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [blockConfirm,  setBlockConfirm]  = useState(null);
  const [toast,         setToast]         = useState("");

  // ─── Firebase Realtime DB listener ────────────────────────────────────────
  // authLoading true iken (Firebase Auth henüz başlatılmamış) listener'ı
  // geciktiriyoruz; bu sayede "user null → playlists boş → loading bitmez"
  // sorununu engelliyoruz.
  useEffect(() => {
    // Auth henüz hazır değilse bekle
    if (authLoading) return;

    setLoading(true);
    setDbError(null);

    const dbRef = ref(db, "playlists");

    const unsub = onValue(
      dbRef,
      // ── Başarılı okuma ──────────────────────────────────────────────────
      (snap) => {
        if (snap.exists()) {
          const list = Object.entries(snap.val())
            .map(([id, v]) => ({ id, ...v }))
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setPlaylists(list);
        } else {
          // /playlists düğümü henüz yok (ilk kullanıcı senaryosu) → boş liste
          setPlaylists([]);
        }
        setLoading(false);
      },
      // ── Okuma hatası (permission-denied, network, vb.) ──────────────────
      (error) => {
        console.error("[BoneBeat] Firebase okuma hatası:", error.code, error.message);
        setDbError(error.message);
        setPlaylists([]);
        setLoading(false); // Sonsuz spinner'ı engelle — hata durumunda da kapat
      }
    );

    return () => unsub();
  }, [authLoading]); // authLoading değişince yeniden bağlan

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  async function handleDelete(id) {
    try {
      await remove(ref(db, `playlists/${id}`));
      if (openPlaylist?.id === id) setOpenPlaylist(null);
      showToast(t('bonebeat.toast.deleted'));
    } catch (err) {
      console.error("[BoneBeat] Silme hatası:", err.code, err.message);
      showToast(t('bonebeat.toast.deleteFailed'));
    }
    setConfirmDelete(null);
  }

  async function handleBlockUser(uid, userName) {
    try {
      await update(ref(db, `users/${uid}`), { status: "blocked" });
      showToast(`${userName || uid} ${t('bonebeat.toast.blocked')}`);
    } catch (err) {
      console.error("[BoneBeat] Engelleme hatası:", err.code, err.message);
      showToast(t('bonebeat.toast.blockFailed'));
    }
    setBlockConfirm(null);
  }

  const visiblePlaylists = view === "mine"
    ? playlists.filter(p => p.uid === user?.uid)
    : playlists;

  // Auth henüz yükleniyorsa (Firebase token henüz gelmedi) spinner göster
  // Böylece "user null → mine filtresi boş → empty state" yanılgısı olmaz
  const isPageLoading = authLoading || loading;

  function openProfile(uid, userName, photoURL) {
    setProfileUser({ uid, userName, photoURL });
    setOpenPlaylist(null);
    setView("userProfile");
  }

  function handleNavigate(key) {
    setView(key);
    setOpenPlaylist(null);
    setProfileUser(null);
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <SidebarBeat activeView={view} onNavigate={handleNavigate} isAdmin={isAdmin} />

      <main className="bb-main" style={{ padding: "64px clamp(20px,4vw,60px) 40px", minHeight: "100vh", overflowY: "auto" }}>

        {/* Toast */}
        {toast && (
          <div style={{
            position: "fixed", bottom: 28, right: 28, zIndex: 9999,
            background: C.bgCard, border: `1px solid ${C.border}`,
            padding: "12px 20px", fontFamily: "'Courier Prime',monospace",
            fontSize: 11, color: C.ivory, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            letterSpacing: "0.06em",
          }}>
            {toast}
          </div>
        )}

        {/* Silme onayı */}
        {confirmDelete && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.ivory, marginBottom: 8 }}>{t('bonebeat.confirm.sure')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldDim, marginBottom: 24 }}>
                "<strong>{confirmDelete.name}</strong>" {t('bonebeat.confirm.deleteBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleDelete(confirmDelete.id)} danger>{t('common.delete')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* Engelleme onayı */}
        {blockConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 8 }}>{t('bonebeat.confirm.blockTitle')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldDim, marginBottom: 24 }}>
                <strong>{blockConfirm.name}</strong> {t('bonebeat.confirm.blockBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setBlockConfirm(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleBlockUser(blockConfirm.uid, blockConfirm.name)} danger>{t('admin.block')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* View routing */}
        {openPlaylist ? (
          <PlaylistDetail
            playlist={openPlaylist} currentUid={user?.uid} isAdmin={isAdmin}
            onClose={() => setOpenPlaylist(null)} onRefresh={() => setOpenPlaylist(null)}
            onOpenProfile={openProfile}
          />
        ) : view === "userProfile" && profileUser ? (
          <UserProfile
            profileUser={profileUser}
            playlists={playlists.filter(p => p.uid === profileUser.uid)}
            onBack={() => { setView("feed"); setProfileUser(null); }}
            onOpen={setOpenPlaylist}
            onDelete={id => setConfirmDelete({ id, name: playlists.find(p => p.id === id)?.playlistName })}
            onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
            isAdmin={isAdmin} currentUid={user?.uid}
          />
        ) : view === "new" ? (
          <NewPlaylistForm currentUser={user} currentUserData={userData} onSuccess={() => setView("feed")} />
        ) : view === "moderation" && isAdmin ? (
          <ModerationPanel
            playlists={playlists}
            onDelete={id => setConfirmDelete({ id, name: playlists.find(p => p.id === id)?.playlistName })}
            onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
          />
        ) : (
          <>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.38em", color: C.goldGhost, textTransform: "uppercase", marginBottom: 6 }}>
                ✦ {view === "mine" ? t('bonebeat.myCollection') : t('bonebeat.archive')} ✦
              </p>
              <h1 style={{ fontFamily: "'Playfair Display',serif", color: C.gold, fontSize: 30, fontWeight: 700, margin: 0 }}>
                {view === "mine" ? t('bonebeat.myPlaylists') : t('bonebeat.archiveTitle')}
              </h1>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <div style={{ height: 1, flex: 1, background: C.border }} />
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.goldGhost, letterSpacing: "0.2em" }}>
                  {isPageLoading ? "…" : `${visiblePlaylists.length} ${t('bonebeat.compilations')}`}
                </span>
              </div>
            </div>

            {isPageLoading ? (
              <div style={{ textAlign: "center", padding: "80px 0" }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", margin: "0 auto 20px",
                  border: "2px solid rgba(212,168,83,0.15)",
                  borderTopColor: "rgba(212,168,83,0.6)",
                  animation: "bb-spin 0.9s linear infinite",
                }} />
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost, letterSpacing: "0.2em", marginBottom: 8 }}>
                  {t('bonebeat.loadingArchive')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(212,168,83,0.2)", letterSpacing: "0.14em" }}>
                  {t('bonebeat.loadingHint')}
                </p>
                <style>{`@keyframes bb-spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : dbError ? (
              /* ── Firebase okuma hatası ── */
              <div style={{
                maxWidth: 480, margin: "60px auto",
                background: C.bgCard, border: `1px solid rgba(224,92,92,0.25)`,
                padding: "40px 36px", textAlign: "center",
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.5 }}>⚠</div>
                <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 10, fontWeight: 700 }}>
                  {t('bonebeat.errorTitle')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "rgba(224,92,92,0.5)", marginBottom: 24, lineHeight: 1.7, letterSpacing: "0.04em", wordBreak: "break-word" }}>
                  {dbError}
                </p>
                <GhostBtn onClick={() => window.location.reload()}>
                  {t('bonebeat.reload')}
                </GhostBtn>
              </div>
            ) : visiblePlaylists.length === 0 ? (
              view === "mine" ? (
                <div style={{
                  maxWidth: 480, margin: "60px auto",
                  background: C.bgCard, border: `1px solid ${C.border}`,
                  padding: "48px 40px", textAlign: "center",
                }}>
                  <div style={{ marginBottom: 24 }}>
                    <svg viewBox="0 0 24 24" fill="none" width={40} height={40} style={{ margin: "0 auto", display: "block", opacity: 0.35 }}>
                      <circle cx="12" cy="12" r="10" stroke={C.gold} strokeWidth="1" />
                      <path d="M12 8v8M8 12h8" stroke={C.gold} strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.goldDim, marginBottom: 10, fontWeight: 700 }}>
                    {t('bonebeat.noPlaylists')}
                  </p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost, marginBottom: 28, lineHeight: 1.8, letterSpacing: "0.04em" }}>
                    {t('bonebeat.noPlaylistsHint')}
                  </p>
                  <PrimaryBtn onClick={() => setView("new")}>✦ {t('bonebeat.createFirst')}</PrimaryBtn>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.goldDim, marginBottom: 8 }}>
                    {t('bonebeat.archiveEmpty')}
                  </p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost, marginBottom: 20 }}>
                    {t('bonebeat.archiveEmptyHint')}
                  </p>
                  <PrimaryBtn onClick={() => setView("new")}>{t('bonebeat.createCompilation')}</PrimaryBtn>
                </div>
              )
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
                {visiblePlaylists.map(pl => (
                  <PlaylistCard
                    key={pl.id} playlist={pl}
                    onOpen={setOpenPlaylist} onOpenProfile={openProfile}
                    onDelete={id => setConfirmDelete({ id, name: pl.playlistName })}
                    onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
                    isAdmin={isAdmin} currentUid={user?.uid}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <style>{`
        ::-webkit-scrollbar { width: 4px; background: ${C.bgDeep}; }
        ::-webkit-scrollbar-thumb { background: rgba(212,168,83,0.15); }
        ::selection { background: rgba(212,168,83,0.2); color: ${C.ivory}; }
      `}</style>
    </div>
  );
}

// ─── Dışa aktarım — SidebarBeatProvider ile sarmalı ──────────────────────────
export default function BoneBeat() {
  return (
    <SidebarBeatProvider>
      <BoneBeatInner />
    </SidebarBeatProvider>
  );
}
